import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageCircle, X, Send, Loader2 } from 'lucide-react';

const UK_PHONE_REGEX = /^(?:(?:\+44\s?|0)(?:7\d{3}|\d{2,4})\s?\d{3}\s?\d{3,4})$/;

function cleanPhone(value) {
  return value.replace(/[^\d+\s]/g, '');
}

function validatePhone(phone) {
  return UK_PHONE_REGEX.test(phone.replace(/\s/g, ''));
}

/**
 * Extract job context from the current page.
 *
 * Derivation:
 * - originPage: always sent as location.pathname (e.g. "/jobs/coach-driver-6931068", "/contact")
 * - originJobRef: the LogicMelon numeric ID extracted from the slug suffix (e.g. "6931068")
 *   Slugs are generated as "{title}-{logicmelon_id}" by the sync function.
 * - originJobTitle: the human-readable part of the slug, de-slugified (e.g. "Coach Driver")
 *   Falls back to the page's document title if available and more descriptive.
 *
 * If the user is not on a /jobs/:slug page, originJobRef and originJobTitle are null.
 */
function extractContext(pathname) {
  const context = { originPage: pathname, originJobRef: null, originJobTitle: null };

  const match = pathname.match(/^\/jobs\/(.+)$/);
  if (!match) return context;

  const slug = match[1];

  // Slug format: "coach-driver-6931068" — numeric ID is always the last segment after final hyphen
  const lastHyphen = slug.lastIndexOf('-');
  if (lastHyphen > 0) {
    const possibleId = slug.slice(lastHyphen + 1);
    if (/^\d+$/.test(possibleId)) {
      context.originJobRef = possibleId;
      // De-slugify the title portion: "coach-driver" → "Coach Driver"
      context.originJobTitle = slug.slice(0, lastHyphen)
        .replace(/-/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase());
    }
  }

  return context;
}

export default function LiveChatIntake() {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [form, setForm] = useState({ name: '', phone: '', message: '', honey: '' });
  const [phoneError, setPhoneError] = useState('');
  const location = useLocation();

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      setForm(f => ({ ...f, phone: cleanPhone(value) }));
      if (phoneError) setPhoneError('');
    } else {
      setForm(f => ({ ...f, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === 'loading') return;

    if (!validatePhone(form.phone)) {
      setPhoneError('Enter a valid UK phone number');
      return;
    }

    setStatus('loading');

    try {
      const context = extractContext(location.pathname);

      const response = await fetch('/.netlify/functions/live-chat-intake', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          phone: form.phone.trim(),
          message: form.message.trim(),
          originPage: context.originPage,
          originJobRef: context.originJobRef,
          originJobTitle: context.originJobTitle,
          honey: form.honey
        })
      });

      if (!response.ok) throw new Error();
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };

  const handleRetry = () => {
    setStatus('idle');
  };

  const handleClose = () => {
    setOpen(false);
    setTimeout(() => {
      setStatus('idle');
      setForm({ name: '', phone: '', message: '', honey: '' });
      setPhoneError('');
    }, 300);
  };

  return (
    <>
      {/* Floating trigger button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 bg-c9-orange hover:bg-c9-orange-alt text-white rounded-full p-4 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 group"
          aria-label="Chat with us"
        >
          <MessageCircle className="h-6 w-6" />
          <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-white text-c9-blue text-sm font-semibold px-3 py-1.5 rounded-lg shadow-md whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
            Chat with us
          </span>
        </button>
      )}

      {/* Chat intake panel */}
      {open && (
        <div className="fixed bottom-6 right-6 z-50 w-[360px] max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden animate-fade-in">
          {/* Header */}
          <div className="bg-c9-blue px-5 py-4 flex items-center justify-between">
            <div>
              <h3 className="text-white font-semibold text-base">Chat with C9</h3>
              <p className="text-gray-300 text-xs mt-0.5">We'll get back to you straight away</p>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-300 hover:text-white transition-colors p-1"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="p-5">
            {/* Success state — chat-bubble style */}
            {status === 'success' && (
              <div className="space-y-3 py-2">
                {/* User's sent message bubble */}
                <div className="flex justify-end">
                  <div className="bg-c9-orange text-white text-sm px-4 py-2.5 rounded-2xl rounded-br-md max-w-[85%]">
                    {form.message || 'Message sent'}
                  </div>
                </div>
                {/* C9 reply bubble */}
                <div className="flex justify-start">
                  <div className="bg-gray-100 text-c9-blue text-sm px-4 py-2.5 rounded-2xl rounded-bl-md max-w-[85%]">
                    <p className="font-semibold">We've got your message — someone's picking this up now 👍</p>
                    <p className="text-gray-500 mt-1.5">They'll be in touch shortly.</p>
                  </div>
                </div>
                <div className="text-center pt-2">
                  <button
                    onClick={handleClose}
                    className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}

            {/* Error state */}
            {status === 'error' && (
              <div className="text-center py-6">
                <p className="text-c9-blue font-medium mb-3">
                  Something went wrong — try again in a sec
                </p>
                <button
                  onClick={handleRetry}
                  className="bg-c9-orange hover:bg-c9-orange-alt text-white px-5 py-2 rounded-lg text-sm font-semibold transition-colors"
                >
                  Try again
                </button>
              </div>
            )}

            {/* Form */}
            {(status === 'idle' || status === 'loading') && (
              <form onSubmit={handleSubmit} className="space-y-3.5">
                {/* Honeypot */}
                <input
                  type="text"
                  name="honey"
                  value={form.honey}
                  onChange={handleChange}
                  style={{ display: 'none' }}
                  tabIndex="-1"
                  autoComplete="off"
                />

                <div>
                  <label htmlFor="lc-name" className="block text-sm font-medium text-gray-700 mb-1">
                    First name
                  </label>
                  <input
                    id="lc-name"
                    name="name"
                    type="text"
                    required
                    value={form.name}
                    onChange={handleChange}
                    disabled={status === 'loading'}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-c9-orange focus:border-transparent disabled:opacity-50"
                    placeholder="Your first name"
                  />
                </div>

                <div>
                  <label htmlFor="lc-phone" className="block text-sm font-medium text-gray-700 mb-1">
                    Phone number
                  </label>
                  <input
                    id="lc-phone"
                    name="phone"
                    type="tel"
                    required
                    value={form.phone}
                    onChange={handleChange}
                    disabled={status === 'loading'}
                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-c9-orange focus:border-transparent disabled:opacity-50 ${phoneError ? 'border-red-400' : 'border-gray-300'}`}
                    placeholder="07xxx xxx xxx"
                  />
                  {phoneError && (
                    <p className="text-red-500 text-xs mt-1">{phoneError}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="lc-message" className="block text-sm font-medium text-gray-700 mb-1">
                    How can we help?
                  </label>
                  <textarea
                    id="lc-message"
                    name="message"
                    required
                    rows={3}
                    value={form.message}
                    onChange={handleChange}
                    disabled={status === 'loading'}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-c9-orange focus:border-transparent disabled:opacity-50 resize-none"
                    placeholder="Looking for work, have a question..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="w-full bg-c9-orange hover:bg-c9-orange-alt text-white py-2.5 rounded-lg font-semibold text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {status === 'loading' ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      Send message
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
